import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, RotateCw, ZoomIn, ZoomOut } from "lucide-react";
import IconButton from "./IconButton";
import cn from "./cn";

/** One wheel notch / button press. */
const STEP = 1.4;
/** Never more than this many screen pixels per image pixel — past it is blur. */
const MAX_PIXEL_SCALE = 8;
const DOUBLE_CLICK_ZOOM = 2.5;
const KEY_PAN = 48;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const FULLSCREEN_SUPPORTED = typeof document !== "undefined"
  && !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);

/**
 * ZoomableImage — an image you can zoom into and move around, in the page.
 *
 * Built for reading small print off a screenshot (a UTR, an amount, a date),
 * where the old answer — open the file's URL in a new tab — sent people out of
 * the LMS to a raw storage link. Everything happens in this frame:
 *
 *   zoom      the buttons, + / −, double-click, pinch, or Ctrl + scroll
 *             (plain scroll in full screen and with `wheel="always"`); always
 *             about the point under the cursor or fingers
 *   pan       drag, or the arrow keys
 *   fit       the percentage button, 0, or double-click when zoomed
 *   rotate    the button or R — a photo of a receipt is often sideways
 *   full      the button or F — the browser's own full screen on THIS element,
 *             so nothing remounts and nothing is fetched again
 *
 * Plain scroll scrolls the page by default because this usually sits inside a
 * scrolling dialog, and a frame that swallowed the wheel would trap it.
 *
 * Geometry: `zoom` is relative to FIT (1 = the whole image visible), the
 * translation is in screen pixels from the frame's centre, and panning is
 * clamped so a larger-than-frame image always covers the frame and a smaller
 * one stays centred — it cannot be flung out of view.
 *
 * The frame needs a height: pass it in `className` (e.g. `tw-h-96`).
 *
 * Props:
 *   src, alt   — the image
 *   className  — the frame's size and placement
 *   wheel      — "ctrl" (default) | "always"
 *   bare       — no border or radius, for a frame that is itself the whole surface
 *   onError    — the image failed to load
 */
const ZoomableImage = ({ src, alt, className = "", wheel = "ctrl", bare = false, onError }) => {
  const frameRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0, rot: 0 });
  const [dragging, setDragging] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  // ── Geometry, kept in a ref so pointer/wheel handlers never read stale state ──
  const sideways = view.rot % 180 !== 0;
  const rw = natural ? (sideways ? natural.h : natural.w) : 0;
  const rh = natural ? (sideways ? natural.w : natural.h) : 0;
  const fit = natural && box.w && box.h ? Math.min(box.w / rw, box.h / rh, 1) : 1;
  const maxZoom = Math.max(1, MAX_PIXEL_SCALE / fit);
  const geo = useRef({});
  geo.current = { fit, rw, rh, bw: box.w, bh: box.h, maxZoom };

  /** Keep a larger image covering the frame, and a smaller one centred. */
  const clampPan = useCallback((v) => {
    const { fit: f, rw: w, rh: h, bw, bh } = geo.current;
    const dw = w * f * v.zoom;
    const dh = h * f * v.zoom;
    const mx = Math.max(0, (dw - bw) / 2);
    const my = Math.max(0, (dh - bh) / 2);
    return { ...v, x: clamp(v.x, -mx, mx), y: clamp(v.y, -my, my) };
  }, []);

  /** Zoom to `next(zoom)` keeping the point (px, py) — from the frame's centre — still. */
  const zoomAt = useCallback((next, px = 0, py = 0) => {
    setView((v) => {
      const zoom = clamp(next(v.zoom), 1, geo.current.maxZoom);
      const k = zoom / v.zoom;
      return clampPan({ ...v, zoom, x: px - (px - v.x) * k, y: py - (py - v.y) * k });
    });
  }, [clampPan]);

  const fromCentre = (clientX, clientY) => {
    const r = frameRef.current.getBoundingClientRect();
    return [clientX - r.left - r.width / 2, clientY - r.top - r.height / 2];
  };

  const reset = useCallback(() => setView((v) => ({ ...v, zoom: 1, x: 0, y: 0 })), []);
  const rotate = useCallback(() => setView((v) => ({ zoom: 1, x: 0, y: 0, rot: (v.rot + 90) % 360 })), []);

  // A new image starts fitted and upright.
  useEffect(() => {
    setNatural(null);
    setView({ zoom: 1, x: 0, y: 0, rot: 0 });
  }, [src]);

  // The frame's size — it changes with the dialog, and on entering full screen.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { setView((v) => clampPan(v)); }, [box, clampPan]);

  // Wheel: a native, non-passive listener — React's is passive and cannot
  // stop the page scrolling under a zoom.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const full = document.fullscreenElement === el || document.webkitFullscreenElement === el;
      if (!(e.ctrlKey || e.metaKey || wheel === "always" || full)) return;
      e.preventDefault();
      const [px, py] = fromCentre(e.clientX, e.clientY);
      // Trackpad pinch arrives as ctrl+wheel with small deltas; a mouse notch is ~100.
      const factor = Math.exp(-e.deltaY * (Math.abs(e.deltaY) < 40 ? 0.01 : 0.0025));
      zoomAt((z) => z * factor, px, py);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [wheel, zoomAt]);

  // Full screen follows the browser's state (Esc, F11, the OS gesture).
  useEffect(() => {
    const onChange = () => {
      const el = frameRef.current;
      setIsFull(!!el && (document.fullscreenElement === el || document.webkitFullscreenElement === el));
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const toggleFull = () => {
    const el = frameRef.current;
    if (!el) return;
    if (isFull) (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    else (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
  };

  // ── Pointer: one finger / the mouse pans, two fingers pinch ──
  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest("[data-zoom-toolbar]")) return;
    frameRef.current.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { type: "pinch", dist: Math.hypot(a.x - b.x, a.y - b.y), zoom: view.zoom };
    } else {
      gesture.current = { type: "pan", sx: e.clientX, sy: e.clientY, x: view.x, y: view.y };
      setDragging(true);
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.type === "pinch" && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const [px, py] = fromCentre((a.x + b.x) / 2, (a.y + b.y) / 2);
      const ratio = Math.hypot(a.x - b.x, a.y - b.y) / (g.dist || 1);
      zoomAt(() => g.zoom * ratio, px, py);
    } else if (g.type === "pan") {
      setView((v) => clampPan({ ...v, x: g.x + (e.clientX - g.sx), y: g.y + (e.clientY - g.sy) }));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      gesture.current = null;
      setDragging(false);
    } else if (pointers.current.size === 1) {
      // Lifting one finger of a pinch carries on as a pan from where it is.
      const [p] = [...pointers.current.values()];
      gesture.current = { type: "pan", sx: p.x, sy: p.y, x: view.x, y: view.y };
    }
  };

  const onDoubleClick = (e) => {
    if (e.target.closest("[data-zoom-toolbar]")) return;
    if (view.zoom > 1.05) { reset(); return; }
    const [px, py] = fromCentre(e.clientX, e.clientY);
    zoomAt(() => DOUBLE_CLICK_ZOOM, px, py);
  };

  const onKeyDown = (e) => {
    const handled = {
      "+": () => zoomAt((z) => z * STEP),
      "=": () => zoomAt((z) => z * STEP),
      "-": () => zoomAt((z) => z / STEP),
      0: reset,
      r: rotate,
      R: rotate,
      f: toggleFull,
      F: toggleFull,
      ArrowLeft: () => setView((v) => clampPan({ ...v, x: v.x + KEY_PAN })),
      ArrowRight: () => setView((v) => clampPan({ ...v, x: v.x - KEY_PAN })),
      ArrowUp: () => setView((v) => clampPan({ ...v, y: v.y + KEY_PAN })),
      ArrowDown: () => setView((v) => clampPan({ ...v, y: v.y - KEY_PAN })),
    }[e.key];
    if (!handled) return;
    e.preventDefault();
    handled();
  };

  const scale = fit * view.zoom;
  const percent = Math.round(scale * 100);
  const zoomed = view.zoom > 1.01;

  return (
    <div
      ref={frameRef}
      className={cn(
        "tw-relative tw-overflow-hidden tw-bg-surface-alt",
        !bare && "tw-rounded-md tw-border tw-border-solid tw-border-line",
        "tw-outline-none focus-visible:tw-shadow-focus",
        zoomed ? (dragging ? "tw-cursor-grabbing" : "tw-cursor-grab") : "tw-cursor-zoom-in",
        // Let a one-finger swipe scroll the page until the image is zoomed.
        zoomed || isFull ? "tw-touch-none" : "tw-touch-pan-y",
        className,
      )}
      tabIndex={0}
      role="group"
      aria-roledescription="zoomable image"
      aria-label={`${alt}. Zoom with plus and minus, move with the arrow keys, 0 to fit, R to rotate, F for full screen.`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        onError={onError}
        className={cn(
          "tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-max-w-none tw-select-none",
          !natural && "tw-invisible",
          !dragging && "tw-transition-transform tw-duration-150 tw-ease-out motion-reduce:tw-transition-none",
        )}
        style={{
          width: natural?.w,
          height: natural?.h,
          transform: `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) rotate(${view.rot}deg) scale(${scale})`,
        }}
      />

      <div
        data-zoom-toolbar
        className="tw-absolute tw-bottom-3 tw-left-1/2 tw-flex tw--translate-x-1/2 tw-items-center tw-gap-0.5 tw-rounded-full tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-1.5 tw-py-1 tw-shadow-popover"
      >
        <IconButton label="Zoom out" icon={ZoomOut} size="sm" variant="ghost" onClick={() => zoomAt((z) => z / STEP)} disabled={!zoomed} />
        <button
          type="button"
          onClick={reset}
          title="Fit to frame (0)"
          className="tw-min-w-[3.25rem] tw-cursor-pointer tw-rounded-sm tw-border-none tw-bg-transparent tw-px-1 tw-py-1 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-content hover:tw-bg-surface-hover"
        >
          {natural ? `${percent}%` : "—"}
        </button>
        <IconButton label="Zoom in" icon={ZoomIn} size="sm" variant="ghost" onClick={() => zoomAt((z) => z * STEP)} disabled={!natural || view.zoom >= maxZoom - 0.01} />
        <span className="tw-mx-0.5 tw-h-4 tw-w-px tw-bg-line-soft" aria-hidden="true" />
        <IconButton label="Rotate" icon={RotateCw} size="sm" variant="ghost" onClick={rotate} />
        {FULLSCREEN_SUPPORTED && (
          <IconButton
            label={isFull ? "Exit full screen" : "Full screen"}
            icon={isFull ? Minimize2 : Maximize2}
            size="sm"
            variant="ghost"
            onClick={toggleFull}
          />
        )}
      </div>
    </div>
  );
};

export default ZoomableImage;
